<template>
  <div class="px-1">
    <WrappedComponent :wrap="!!account">
      <template #wrapper>
        <!-- Can't use v-if because this wrapper tag must be renderable, even if wrap is false -->
        <a
          :href="salesforceUrl(account && account.id)"
          target="_blank"
          rel="noopener"
          class="text-decoration-none d-inline-block"
        />
      </template>
      <AppHeading v-text="ticket.requester.name" />
      <div
        class="mt-n2 mb-2 caption grey--text"
        v-text="ticket.requester.email"
      />
    </WrappedComponent>
    <VList dense class="pt-0">
      <!-- Negative right margin allows tags to reach right edge despite their right margins for spacing -->
      <div class="mr-n2">
        <ChipLabel
          v-if="account && account.vip"
          color="purple"
          icon="$user-crown"
          outlined
          small
        >
          VIP
        </ChipLabel>
        <ChipLabel
          v-if="account && account.personHasOptedOutOfEmail"
          color="red"
          icon="$envelope"
          outlined
          small
        >
          Email Opt Out
        </ChipLabel>
        <!-- Darken tooltip color to make text more legible -->
        <ChipLabel
          v-if="firstSeen"
          icon="$user"
          outlined
          small
          tooltip-color="grey darken-4"
          tooltip-analytics-name="Customer timeline"
        >
          Customer for {{ firstSeen ? formatDistanceToNow(firstSeen) : '?' }}
          <template #tooltip>
            <TimelineDense
              :items="customerTimelineItems"
              class="mx-n4 my-n1"
              style="width: 95vw"
              dark
            />
          </template>
        </ChipLabel>
        <ChipLabel
          v-if="account && account.weddingDate"
          icon="$rings-wedding"
          outlined
          small
        >
          Wedding <span class="grey--text mx-1">•</span>
          {{ formatDateShortWithYear(calendarToDateObj(account.weddingDate)) }}
        </ChipLabel>
        <!-- TODO: Show labels for categories the customer is interested in based on activity or history -->
        <ChipLabel
          v-if="account && account.orderCount"
          icon="$cash-register"
          outlined
          small
        >
          {{ pluralize(account.orderCount, 'order') }}
          <span class="grey--text mx-1">•</span>
          {{ formatCurrency(account.amountSpent) }}
        </ChipLabel>
        <ChipLabel
          v-if="cartCount"
          icon="$shopping-cart"
          outlined
          small
          tooltip-analytics-name="Cart count"
        >
          {{ cartCount }} in cart<sup>*</sup>
          <template #tooltip>
            *Some of these proofs may have been moved to Saved For Later, after
            initially getting approved, or having edits requested.
          </template>
        </ChipLabel>
        <ChipLabel v-if="savedCount" icon="$save" outlined small>
          {{ savedCount }} saved
        </ChipLabel>
        <ChipLabel v-if="canceledCount" icon="$trash" outlined small>
          {{ canceledCount }} deleted
        </ChipLabel>
        <ChipLabel
          v-if="ratings.length"
          outlined
          small
          tooltip-color="grey darken-4"
          tooltip-analytics-name="Ratings timeline"
        >
          <span>Satisfaction</span>
          <span class="grey--text ml-1">•</span>
          <!--
          Only show most recent 5 ratings to avoid overwhelming the limited space available.
          All ratings are displayed in the timeline tooltip.
          -->
          <div v-for="(rating, i) in ratings.slice(-5)" :key="'rating-' + i">
            <div
              :class="[
                {
                  'green--text': isRatingGood(rating),
                  'red--text': isRatingBad(rating),
                  'orange--text': !isRatingGood(rating) && !isRatingBad(rating)
                },
                'd-inline-block',
                'ml-1'
              ]"
            >
              <!-- Icon appears too low when in a container; margin-bottom compensates and fixes alignment with text -->
              <VIcon x-small style="margin-bottom: 1px">
                <template v-if="isRatingGood(rating)">$smile</template>
                <template v-else-if="isRatingBad(rating)">$frown</template>
                <template v-else>$meh</template>
              </VIcon>
            </div>
          </div>
          <template #tooltip>
            <TimelineDense
              :items="ratingsTimelineItems"
              class="mx-n4 my-n1"
              style="width: 95vw"
              dark
            />
          </template>
        </ChipLabel>
      </div>
      <template v-if="account && account.customerSpecialInstructions.length">
        <VSubheader class="px-0">Customer special instructions</VSubheader>
        <SpecialInstructionListItems
          :special-instructions="account.customerSpecialInstructions"
        />
      </template>
      <TicketsSummary :tickets="ticketsForCurrentRequester" />
    </VList>
  </div>
</template>

<script lang="ts">
import { formatDistanceToNow } from 'date-fns'
import { capitalize, sortBy } from 'lodash-es'
import { calendarToDateObj } from 'ts-force'
import { computed, defineComponent, PropType } from 'vue'

import AppHeading from '@/components/AppHeading.vue'
import ChipLabel from '@/components/ChipLabel.vue'
import { formatCurrency } from '@/components/mixins/currency'
import { formatDateShortWithYear } from '@/components/mixins/dateFormatters'
import { pluralize } from '@/components/mixins/pluralize'
import { ticketStatusColors } from '@/components/mixins/tickets'
import SpecialInstructionListItems from '@/components/SpecialInstructionListItems.vue'
import TicketsSummary from '@/components/TicketsSummary.vue'
import TimelineDense, { TimelineItem } from '@/components/TimelineDense.vue'
import { Account, Order as SalesforceOrder, Proof } from '@/generated'
import { salesforceUrl } from '@/store/modules/salesforce'
import { Ticket as RestTicket } from '@/store/modules/types/zendeskRest'
import Ticket = ZAFClient.Ticket

export default defineComponent({
  components: {
    SpecialInstructionListItems,
    AppHeading,
    ChipLabel,
    TicketsSummary,
    TimelineDense
  },
  props: {
    account: {
      type: Object as PropType<Account | null>,
      default: null
    },
    salesforceOrders: {
      type: Array as PropType<SalesforceOrder[]>,
      required: true
    },
    proofs: {
      type: Array as PropType<Proof[]>,
      required: true
    },
    ticket: {
      type: Object as PropType<Ticket>,
      required: true
    },
    tickets: {
      type: Array as PropType<RestTicket[]>,
      required: true
    },
    originalRequesterEmail: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    interface Rating {
      date: Date
      source: 'Zendesk' | 'Delighted' | 'Negative'
      score: 'good' | 'bad' | number
      name?: string
      comment?: string
    }

    const isRatingGood = (rating: Rating) =>
      rating.score === 'good' || rating.score >= 9
    const isRatingBad = (rating: Rating) =>
      rating.score === 'bad' || rating.score <= 6
    const isRatingMeh = (rating: Rating) =>
      !isRatingGood(rating) && !isRatingBad(rating)

    // Only use ticket data when viewing the original ticket requester,
    // not when searching for a different customer via SearchBox
    const ticketsForCurrentRequester = computed(() => {
      if (
        !props.originalRequesterEmail ||
        props.ticket.requester.email === props.originalRequesterEmail
      ) {
        return props.tickets
      }
      return []
    })

    const customerTimelineItems = computed(() => {
      const items: TimelineItem[] = []
      const { account } = props
      const tickets = ticketsForCurrentRequester.value

      if (account) {
        if (
          account.emailCollectionDate &&
          (!account.registrationDate ||
            account.emailCollectionDate.getTime() !==
              account.registrationDate.getTime())
        ) {
          items.push({
            title: 'Signed up for emails',
            date: account.emailCollectionDate,
            icon: '$envelope'
          })
        }
        if (account.registrationDate) {
          items.push({
            title: 'Registered',
            date: account.registrationDate,
            icon: '$user'
          })
        }
      }
      const addTicket = (ticket: RestTicket) =>
        items.push({
          title: ticket.subject,
          caption: ticket.description,
          icon: '$question-circle',
          date: new Date(ticket.createdAt),
          color: ticketStatusColors[ticket.status].color
        })
      if (tickets.length) {
        addTicket(tickets[0])
      }
      if (tickets.length > 4) {
        items.push({
          title: `${tickets.length - 3} tickets from ${formatDateShortWithYear(
            tickets[1].createdAt
          )}…`,
          date: new Date(tickets[tickets.length - 3].createdAt),
          icon: '$question-circle',
          color: ticketStatusColors['closed'].color
        })
      } else if (tickets.length === 4) {
        addTicket(tickets[tickets.length - 3])
      }
      if (tickets.length >= 3) {
        addTicket(tickets[tickets.length - 2])
      }
      if (tickets.length >= 2) {
        addTicket(tickets[tickets.length - 1])
      }
      return sortBy(items, (item) => item.date)
    })

    const firstSeen = computed(() => customerTimelineItems.value[0]?.date)

    const cartCount = computed(() => {
      let count = 0
      props.proofs.forEach((proof) => {
        if (
          proof.proofId === proof.project?.projectId &&
          proof.paid === false &&
          proof.status !== 'Cancelled' &&
          proof.status !== 'Saved'
        ) {
          count++
        }
      })
      return count
    })

    const savedCount = computed(() => {
      let count = 0
      props.proofs.forEach((proof) => {
        if (
          proof.proofId === proof.project?.projectId &&
          proof.status == 'Saved'
        ) {
          count++
        }
      })
      return count
    })

    const canceledCount = computed(() => {
      let count = 0
      props.proofs.forEach((proof) => {
        if (
          proof.proofId === proof.project?.projectId &&
          proof.status == 'Cancelled'
        ) {
          count++
        }
      })
      return count
    })

    const ratings = computed(() => {
      const result: Rating[] = []
      const { account } = props
      const tickets = ticketsForCurrentRequester.value
      tickets.forEach((ticket) => {
        if (
          ticket.satisfactionRating.score == 'good' ||
          ticket.satisfactionRating.score == 'bad'
        ) {
          result.push({
            date: new Date(ticket.createdAt),
            source: 'Zendesk',
            score: ticket.satisfactionRating.score,
            comment:
              (ticket.satisfactionRating.reason !== 'No reason provided' &&
                ticket.satisfactionRating.reason) ||
              undefined
          })
        }
      })
      account?.delightedResponses?.forEach((delightedResponse) => {
        if (delightedResponse.timestamp && delightedResponse.score) {
          result.push({
            date: new Date(delightedResponse.timestamp),
            score: delightedResponse.score,
            source: 'Delighted',
            comment: delightedResponse.comment || undefined
          })
        }
      })
      account?.negatives?.forEach((negative) => {
        if (negative.createdDate && negative.name) {
          result.push({
            date: new Date(negative.createdDate),
            score: 'bad',
            source: 'Negative',
            name:
              negative.name +
              (negative.category ? ` (${negative.category})` : ''),
            comment: negative.description || undefined
          })
        }
      })
      return sortBy(result, (rating) => rating.date.getTime())
    })

    const ratingsTimelineItems = computed(() =>
      ratings.value.map((rating) => {
        let title
        switch (rating.source) {
          case 'Delighted':
            title = `${rating.score}/10 NPS score from Delighted`
            break
          case 'Negative':
            title = `${rating.name} from customer negative`
            break
          case 'Zendesk':
            title = `${capitalize(
              rating.score as string
            )} rating from Zendesk satisfaction survey`
            break
        }
        let color
        let icon
        if (isRatingGood(rating)) {
          color = 'green'
          icon = '$smile'
        } else if (isRatingBad(rating)) {
          color = 'red'
          icon = '$frown'
        } else {
          color = 'orange'
          icon = '$meh'
        }
        return {
          title,
          caption: rating.comment,
          color,
          date: rating.date,
          icon
        } as TimelineItem
      })
    )

    return {
      formatCurrency,
      formatDateShortWithYear,
      formatDistanceToNow,
      pluralize,
      salesforceUrl,
      calendarToDateObj,
      ticketsForCurrentRequester,
      customerTimelineItems,
      firstSeen,
      cartCount,
      savedCount,
      canceledCount,
      ratings,
      ratingsTimelineItems,
      isRatingGood,
      isRatingBad,
      isRatingMeh
    }
  }
})
</script>
